/* global DfApi */

import { appFor } from '@/js/util/tiptap/extensions/_get_menu_control';
import { timeElapsed } from '@/js/util/tiptap/extensions/_time_elapsed';
import { Extension } from '@tiptap/core';

const api = () => new DfApi();
const enabledMessage = '<i class="fa-regular fa-circle-check"></i>autosave enabled';
const savingMessage = '<span class="spinner"></span> Saving';
const saveErrorMessage = `<div title="If the error persists please contact customer service">
  <i class="fa-regular fa-circle-exclamation"></i> Autosave error! Retrying...
</div>`;

const AutoSaveContract = Extension.create({
  name: 'autoSaveContract',
  onCreate({ editor }) {
    const app = appFor(editor);

    if (shouldEnableAutosave(editor, app.dataset)) {
      editor.storage.autosave = true;
      storeModelReferences(editor, app.dataset);
      scheduleUpdateSavedAt(editor);
      trackTitleAndSignatureChanges(editor);
      trackClientChanges(editor);
      setSaveInfo(editor, enabledMessage);
    }
  },
  onUpdate({ editor }) {
    editor.autosave && scheduleSaveContract(editor);
  },
});

const shouldEnableAutosave = (editor, options) => {
  const isTemplate = options.tiptapEditorPresetId || null;
  const isContract = options.tiptapEditorContractId || null;
  const inProject = options.tiptapEditorProjectId || null;

  return isTemplate || (isContract && inProject);
};

const storeModelReferences = (editor, options) => {
  editor.storage.projectId = options.tiptapEditorProjectId || null;
  editor.storage.contractId = options.tiptapEditorContractId || null;
  editor.storage.templateId = options.tiptapEditorPresetId || null;
};

const scheduleUpdateSavedAt = (editor) => {
  const updateInfoTimerId = editor.storage.updateInfoTimerId;

  if (updateInfoTimerId) {
    window.clearTimeout(updateInfoTimerId);
  }
  editor.storage.updateInfoTimerId = window.setInterval(updateSavedAt, 60000, editor);
};

const updateSavedAt = (editor, withIcon = false) => {
  const { lastSavedAt } = editor.storage;
  if (!lastSavedAt) {
    return;
  }

  const date = lastSavedAt.toLocaleDateString('en-US');
  const time = lastSavedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' });
  const icon = '<i class="fa-regular fa-circle-check"></i>';
  const message = `${icon}${timeElapsed(lastSavedAt)} ago`;

  setSaveInfo(editor, `<div title="${date} at ${time}">${message}</div>`);
};

const trackTitleAndSignatureChanges = (editor) => {
  editor.options.element.closest('form').addEventListener('input', (_event) => {
    scheduleSaveContract(editor);
  });
};

const trackClientChanges = (editor) => {
  editor.storage.clientChangesArmed = false;
  window.setTimeout(() => {
    editor.storage.clientChangesArmed = true;
  }, 0);

  editor.options.element
    .closest('form')
    .addEventListener('clients--client-selector:change', (_event) => {
      editor.storage.clientChangesArmed && scheduleSaveContract(editor);
    });
};

const scheduleSaveContract = (editor, timeout = 2000) => {
  const autoSaveTimerId = editor.storage.autoSaveTimerId;

  if (autoSaveTimerId) {
    window.clearTimeout(autoSaveTimerId);
  }
  editor.storage.autoSaveTimerId = window.setTimeout(saveContract, timeout, editor);
};

const designerSignature = (editor) => {
  return editor.options.element
    .closest('form')
    .querySelector('input[name="contract[designer_signature]"]').value;
};
const contractTitle = (editor) => {
  const name = editor.storage.templateId ? 'preset[title]' : 'contract[title]';
  return editor.options.element.closest('form').querySelector(`input[name="${name}"]`).value;
};

const persistContent = (editor) => {
  const dfApi = api();
  const contract = buildContractObject(editor);

  if (editor.storage.templateId) {
    return dfApi.saveContractTemplate(editor.storage.templateId, contract);
  }
  return dfApi.saveContract(editor.storage.projectId, editor.storage.contractId, contract);
};

const buildContractObject = (editor) => {
  const content = editor.getHTML();
  const contract = { details: content, title: contractTitle(editor) };
  if (editor.storage.contractId) {
    contract['designer_signature'] = designerSignature(editor);
  }

  return contract;
};

const saveContract = async (editor) => {
  scheduleUpdateSavedAt(editor); // reset the last saved message timer
  setSaveInfo(editor, savingMessage);

  try {
    const response = await persistContent(editor);

    response.error ? saveContractError(editor) : saveContractSuccess(editor, response);
  } catch (error) {
    console.error('saveContract: failed to save contract', error);
    saveContractError(editor);
  }
};

const setSaveInfo = (editor, message) => {
  const editorContainer = editor.view.dom.closest('[data-controller="tiptap-editor"]');
  const autoSaveElement = editorContainer.querySelector('[data-js-auto-save]');

  autoSaveElement.innerHTML = message;
};

const saveContractSuccess = async (editor, response) => {
  try {
    editor.storage.lastSavedAt = new Date();
  } catch (error) {
    console.error('saveContractSuccess: error parsing saved date', error);
    editor.storage.lastSavedAt = new Date();
  }
  updateSavedAt(editor, true);
};

const saveContractError = (editor) => {
  setSaveInfo(editor, saveErrorMessage);
  scheduleSaveContract(editor, 10000);
};

export default [AutoSaveContract];
